import dayjs from "dayjs"
import { useCallback, useMemo } from "react"

import { classAtoms } from "#atoms"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { StudentClassAttendance } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { Attendance } from "#customTypes/types"
import { useIsFulfilled } from "#hooks/jotai/useCascadingData"
import { useSomeDocs } from "#hooks/jotai/useSubscription"
import { useGetOneUser } from "#hooks/newHooks/useGetUser"
import { generateSessionDates, isDefined } from "#utils/classHelpers"
import { setTimeFromString } from "#utils/dateHelpers"

// Reuses StudentClassAttendance type since teacher attendance has same schema
export type TeacherClassAttendance = StudentClassAttendance

const useTeacherAttendanceData = (userId: Uuid) => {
  const user = useGetOneUser(userId)

  const teacherAttendance = user?.teacherAttendance

  const classIds = useMemo(() => {
    return Object.keys(teacherAttendance || {}).sort()
  }, [teacherAttendance])

  const classData = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classIds
  )

  const isFulfilled = useIsFulfilled(classIds, classData)

  // Newest classes first
  const reverseSortedClassData = useMemo(() => {
    if (!isFulfilled) return []

    return classData?.slice().sort((a, b) => {
      return dayjs(b.startDate).diff(dayjs(a.startDate))
    })
  }, [classData, isFulfilled])

  const getSortedClassAttendance = useCallback(
    (attendance: Attendance): TeacherClassAttendance[] => {
      if (!reverseSortedClassData || !attendance) return []

      return reverseSortedClassData
        .filter((classObj) => attendance[classObj.id])
        .map((classObj) => {
          const sessionDates = generateSessionDates({
            startDate: dayjs(classObj.startDate),
            times: [
              setTimeFromString(classObj.startDate, classObj.startTime)!,
              setTimeFromString(classObj.startDate, classObj.endTime)!,
            ],
            isSummerCamp: classObj.isSummerCamp ?? false,
            numberOfSessions: classObj?.sessionIds?.length ?? 0,
          }).map((session) => dayjs(session.eventStart))

          const attendanceData = attendance[classObj.id]

          const attendanceWithDates = attendanceData.map(
            (session, sessionIndex) => {
              return {
                ...session,
                sessionDate: sessionDates[sessionIndex],
              }
            }
          )

          return {
            classId: classObj.id,
            isSummerCamp: !!classObj.isSummerCamp,
            startDate: classObj.startDate,
            name: classObj.name,
            attendanceSessions: attendanceWithDates,
          }
        })
        .filter(isDefined)
    },
    [reverseSortedClassData]
  )

  const sortedTeacherAttendance = useMemo(() => {
    return getSortedClassAttendance(teacherAttendance || {})
  }, [teacherAttendance, getSortedClassAttendance])

  return {
    user,
    sortedTeacherAttendance,
    isFulfilled,
  }
}

export default useTeacherAttendanceData
